import { Check, ChevronDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { makeTr, type LandingLocale, localePath, pick } from "@/lib/landing/locale";
import {
  FEATURES,
  PLANS,
  allFeatures,
  formatCzk,
  getPlan,
  limitChips,
  planSignupPath,
  vatPayer,
  type Plan,
} from "@/lib/pricing/plans";
import { FoundingOfferNote } from "@/components/billing/FoundingOfferNote";
import { Container, CtaLink, Section, SectionHeading } from "./primitives";

/** The plan button: "get it", with the plan name in the right Czech case. */
const GET_PLAN: Record<Plan["id"], { cs: string; en: string }> = {
  start: { cs: "Získat Start", en: "Get Start" },
  firma: { cs: "Získat Firmu", en: "Get Firma" },
};

function CheckMark({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[hsl(var(--action))] dark:text-primary",
        className
      )}
    >
      <Check className="size-3" strokeWidth={3} />
    </span>
  );
}

function PlanCard({ plan, locale }: { plan: Plan; locale: LandingLocale }) {
  const tr = makeTr(locale);
  const titleId = `plan-${plan.id}`;
  const perMonth = tr("/ měsíc", "/ month");

  return (
    <article
      aria-labelledby={titleId}
      className={cn(
        "relative flex flex-col rounded-2xl border bg-card p-6 sm:p-8",
        plan.recommended
          ? "border-[hsl(var(--action))] shadow-[0_0_0_1px_hsl(var(--action)/0.4),0_24px_48px_-24px_hsl(var(--action)/0.45)] dark:border-primary dark:shadow-[0_0_0_1px_hsl(var(--primary)/0.35),0_24px_64px_-24px_hsl(var(--primary)/0.35)]"
          : "shadow-[0_16px_40px_-24px_rgb(0_0_0/0.25)]"
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <h3 id={titleId} className="text-xl font-semibold tracking-tight">
          {plan.name}
        </h3>
        {plan.recommended && (
          <span className="rounded-full bg-[hsl(var(--action))] px-2.5 py-1 text-xs font-semibold text-[hsl(var(--action-foreground))] dark:bg-primary dark:text-primary-foreground">
            {tr("Doporučujeme", "Recommended")}
          </span>
        )}
      </div>

      <p className="mt-5 flex items-baseline gap-2">
        <span className="text-5xl font-semibold tabular-nums tracking-tight">
          {formatCzk(plan.priceMonthlyCzk)}
        </span>
        <span className="text-sm text-muted-foreground">{perMonth}</span>
      </p>
      <p className="mt-4 text-sm font-medium">{pick(plan.audience, locale)}</p>

      <div className="mt-6 flex-1 border-t pt-6">
        {plan.includes && (
          <p className="mb-3 text-sm font-semibold">
            {tr(
              `Vše ze ${getPlan(plan.includes).name}, navíc:`,
              `Everything in ${getPlan(plan.includes).name}, plus:`
            )}
          </p>
        )}
        <ul className="space-y-2.5 text-[15px]">
          {plan.highlights.map((id) => (
            <li key={id} className="flex gap-3">
              <CheckMark />
              <span>{pick(FEATURES[id].short, locale)}</span>
            </li>
          ))}
        </ul>
      </div>

      <ul
        aria-label={tr("Limity tarifu", "Plan limits")}
        className="mt-6 flex flex-wrap gap-1.5 text-xs tabular-nums"
      >
        {limitChips(plan, locale).map((chip) => (
          <li
            key={chip}
            className="rounded-md border bg-secondary/60 px-2 py-1 dark:bg-secondary/40"
          >
            {chip}
          </li>
        ))}
      </ul>

      <CtaLink
        to={localePath(locale, planSignupPath(plan))}
        variant={plan.recommended ? "primary" : "solid"}
        arrow
        className={cn(
          "mt-6 h-12 w-full text-base",
          plan.recommended &&
            "shadow-[0_10px_30px_-10px_hsl(var(--action)/0.7)] dark:shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.7)]"
        )}
      >
        {GET_PLAN[plan.id][locale === "cs" ? "cs" : "en"]}
      </CtaLink>
    </article>
  );
}

/** "Porovnat tarify": every feature and limit, for people who want the detail. */
function CompareTable({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);
  const [start, firma] = PLANS;
  const startFeatures = allFeatures(start);
  const group = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0");
  const limits: [string, (p: Plan) => number][] = [
    [tr("Firmy", "Companies"), (p) => p.limits.companies],
    [tr("Účty Fio", "Fio accounts"), (p) => p.limits.fioAccounts],
    [tr("Uživatelé", "Users"), (p) => p.limits.users],
    [
      tr("Doklady pro AI měsíčně", "AI-read documents a month"),
      (p) => p.limits.aiDocumentsPerMonth,
    ],
  ];
  const yes = (
    <>
      <CheckMark className="mx-auto" />
      <span className="sr-only">{tr("ano", "yes")}</span>
    </>
  );
  const no = (
    <>
      <Minus aria-hidden="true" className="mx-auto size-4 text-muted-foreground/50" />
      <span className="sr-only">{tr("ne", "no")}</span>
    </>
  );

  return (
    <details className="group/compare mx-auto mt-8 max-w-4xl">
      <summary className="mx-auto flex w-fit cursor-pointer list-none items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-[hsl(var(--action))] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--action))] dark:text-primary dark:focus-visible:ring-primary [&::-webkit-details-marker]:hidden">
        {tr("Porovnat tarify", "Compare plans")}
        <ChevronDown
          aria-hidden="true"
          className="size-4 transition-transform group-open/compare:rotate-180"
        />
      </summary>
      <div className="mt-4 overflow-x-auto rounded-xl border bg-card">
        <table className="w-full min-w-[30rem] text-sm">
          <thead>
            <tr className="bg-secondary/50">
              <th scope="col" className="px-4 py-3 text-left font-medium">
                <span className="sr-only">{tr("Funkce", "Feature")}</span>
              </th>
              {PLANS.map((p) => (
                <th key={p.id} scope="col" className="w-24 px-4 py-3 text-center font-semibold">
                  {p.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {limits.map(([name, value]) => (
              <tr key={name}>
                <th scope="row" className="px-4 py-2.5 text-left font-normal text-muted-foreground">
                  {name}
                </th>
                {PLANS.map((p) => (
                  <td key={p.id} className="px-4 py-2.5 text-center tabular-nums">
                    {group(value(p))}
                  </td>
                ))}
              </tr>
            ))}
            {allFeatures(firma).map((id) => (
              <tr key={id}>
                <th scope="row" className="px-4 py-2.5 text-left font-normal">
                  {pick(FEATURES[id].short, locale)}
                </th>
                <td className="px-4 py-2.5 text-center">{startFeatures.includes(id) ? yes : no}</td>
                <td className="px-4 py-2.5 text-center">{yes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

/** Pricing, rendered only from src/lib/pricing/plans.ts. */
export function PricingSection({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);
  const trialDays = PLANS[0].trialDays;
  const note = [
    !vatPayer && tr("Nejsme plátci DPH, cena je konečná", "Not VAT registered: the price is final"),
    trialDays !== null &&
      tr(`${trialDays} dní zdarma, bez karty`, `${trialDays} days free, no card`),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Section id="cenik" labelledBy="pricing-title" contours="field">
      <Container>
        <SectionHeading
          id="pricing-title"
          eyebrow={tr("Ceník", "Pricing")}
          title={tr("Dva tarify. Párování je v obou.", "Two plans. Matching comes with both.")}
          lead={tr(
            "Firma přidává fakturaci, AI asistenty a více firem.",
            "Firma adds invoicing, AI assistants and several companies."
          )}
          align="center"
        />

        <div className="mx-auto mt-10 grid max-w-4xl gap-5 md:grid-cols-2">
          {PLANS.map((plan) => (
            <PlanCard key={plan.id} plan={plan} locale={locale} />
          ))}
        </div>

        {/* Only after load and while places are left (never in the prerendered markup). */}
        <FoundingOfferNote
          language={locale === "cs" ? "cs" : "en"}
          className="mx-auto mt-6 max-w-4xl justify-center text-center"
        />
        <p className="mt-6 text-center text-[13px] text-muted-foreground">{note}</p>
        <CompareTable locale={locale} />
      </Container>
    </Section>
  );
}
